import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export type MessageChannel = "email" | "sms" | "push";
// "sending" is the claim step's brief in-flight reservation, set by
// claim_scheduled_message_for_send() between claiming a scheduled row and
// the provider call completing (see process-scheduled-messages) -- distinct
// from "pending" (not yet due/claimed).
export type MessageStatus = "pending" | "sending" | "sent" | "delivered" | "bounced" | "failed";
export type MessageDirection = "outbound" | "inbound";

export interface MessageInsert {
  organization_id: string;
  sender_id: string;
  recipient_person_id: string | null;
  recipient_request_id: string | null;
  channel: MessageChannel;
  direction: MessageDirection;
  recipient_address: string;
  subject: string | null;
  body: string;
  status: MessageStatus;
  sent_at: string | null;
  scheduled_at: string | null;
  automation_trigger?: string | null;
  template_version?: string | null;
  suppression_status?: string | null;
  metadata?: Json | null;
}

export interface MessageRecord extends Omit<MessageInsert, 'metadata'> {
  id: string;
  created_at: string;
  updated_at: string;
  delivered_at: string | null;
  error_message: string | null;
  is_system: boolean;
  metadata: Json | null;
  // Server-set only -- authenticated/anon INSERT/UPDATE on this column is
  // revoked (20260927003000_messages_literal_subject_provenance.sql), so
  // it's deliberately absent from MessageInsert: no client-constructed
  // insert payload can set it.
  subject_is_literal: boolean;
  // Server-set only, same protection as subject_is_literal above -- neither is granted to
  // authenticated/anon (20260927230000/20260927240000_*.sql). Unlike subject_is_literal
  // (which legitimately resets on a subject edit), these two are immutable historical facts
  // about which system dispatched the row and never change once set.
  comm_send_owned: boolean;
  comm_message_id: string | null;
}

export async function createMessageRecord(messageData: MessageInsert) {
  const { data, error } = await supabase
    .from("messages")
    .insert(messageData)
    .select()
    .single();

  if (error) {
    console.error("Error sending message:", error);
    throw error;
  }

  return data as MessageRecord;
}

export async function createMessageRecords(messagesData: MessageInsert[]) {
  const { data, error } = await supabase
    .from("messages")
    .insert(messagesData)
    .select();

  if (error) {
    console.error("Error sending bulk messages:", error);
    throw error;
  }

  return data as MessageRecord[];
}
